import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDragLayer } from 'react-dnd';
import { fromUnixTime } from 'date-fns';

import { funnelResultQuery } from '../../queries';
import { useSelectedSite, useStableValue, useTimeZonedFunctions } from '/src/hooks';
import { useFunnelEditorContext } from '../../funnel-editor-context';
import { DateRangeRow } from './DateRangeRow';
//import { getFakeFunnelData } from '../../fake-data';
import { MainChart } from './MainChart';
import { TimeChart } from './TimeChart';
import { FunnelResultTable } from './FunnelResultTable';
import { getFunnelStartedAt } from '../../utils';

export function FunnelResults({ stepsData, goals }) {
  const { date, setDateRange } = useFunnelEditorContext();
  const { selectedSite } = useSelectedSite();
  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));
  const stableStepsData = useStableValue(stepsData, isDragging);

  const { getConvertedDateRange, getDefaultDateRange, startOfDay, endOfDay, isBefore, isAfter } =
    useTimeZonedFunctions();
  const funnelStartedAt = useMemo(
    () => getFunnelStartedAt(stableStepsData, selectedSite),
    [selectedSite, stableStepsData],
  );
  const defaultDateRange = useMemo(
    () => getDefaultDateRange({ featureStartedAt: funnelStartedAt ?? undefined }),
    [funnelStartedAt, getDefaultDateRange],
  );
  const effectiveDateRange = date ?? defaultDateRange;
  const pickerLowerBoundary = useMemo(() => startOfDay(funnelStartedAt ?? new Date()), [funnelStartedAt, startOfDay]);
  const pickerUpperBoundary = useMemo(() => endOfDay(new Date()), [endOfDay]);

  useEffect(() => {
    if (!date) return;

    const { startDate, endDate } = getConvertedDateRange(date);
    const parsedStart = fromUnixTime(startDate);
    const parsedEnd = fromUnixTime(endDate);

    if (!isBefore(parsedStart, pickerLowerBoundary) && !isAfter(parsedEnd, pickerUpperBoundary)) {
      return;
    }

    setDateRange({
      start_date: isBefore(parsedStart, pickerLowerBoundary) ? pickerLowerBoundary : parsedStart,
      end_date: isAfter(parsedEnd, pickerUpperBoundary) ? pickerUpperBoundary : parsedEnd,
    });
  }, [date, getConvertedDateRange, isAfter, isBefore, pickerLowerBoundary, pickerUpperBoundary, setDateRange]);

  const convertedDateRange = useMemo(
    () => getConvertedDateRange(effectiveDateRange),
    [effectiveDateRange, getConvertedDateRange],
  );

  const { data, isFetching } = useQuery({
    ...funnelResultQuery({
      siteId: selectedSite.id,
      startAt: convertedDateRange.startDate,
      endAt: convertedDateRange.endDate,
      timeUnit: convertedDateRange.timeUnit,
      steps: stableStepsData.map(({ id, type }) => ({ id, type })),
    }),
    enabled: !isDragging,
  });
  /*const data = useMemo(
    () =>
      getFakeFunnelData({
        steps: stableStepsData.map(({ id, type }) => ({ id, type })),
        dateRange: { startDate: convertedDateRange.startDate, endDate: convertedDateRange.endDate },
        timeUnit: convertedDateRange.timeUnit,
      }),
    [stableStepsData, convertedDateRange],
  );
  const isFetching = false;*/

  const stepsResults = useMemo(() => data?.funnelResult?.steps ?? [], [data]);
  const timeUnitResults = useMemo(() => data?.funnelResult?.timeUnitResults ?? [], [data]);

  // Currency supposed to be the same for the whole query, so we just take it once and use that
  const currency = useMemo(() => data?.funnelResult?.steps?.[0]?.value?.displayCurrency ?? 'USD', [data]);

  return (
    <section className="grow pl-5 pr-10 py-10 min-w-0">
      <DateRangeRow
        dateRange={effectiveDateRange}
        lowerBoundary={pickerLowerBoundary}
        upperBoundary={pickerUpperBoundary}
      />
      <MainChart loading={isFetching} stepsResults={stepsResults} stepsData={stableStepsData} currency={currency} />
      <TimeChart
        loading={isFetching}
        stepsData={stableStepsData}
        timeUnit={convertedDateRange.timeUnit}
        timeUnitResults={timeUnitResults}
        currency={currency}
      />
      <FunnelResultTable
        loading={isFetching}
        stepsResults={stepsResults}
        stepsData={stableStepsData}
        goals={goals}
        currency={currency}
      />
    </section>
  );
}
